// Cart drawer wired to real WooCommerce data: opens on any add-to-cart
// (including the native WC 'added_to_cart' jQuery event, fired by WC's own
// core add-to-cart AJAX script on every archive/product page), and talks to
// the fc_cart_* AJAX actions in includes/ajax.php for qty/remove/coupon so
// the drawer never needs a full page reload.

import { getRealJQuery } from '../wp-jquery';

function ajaxUrl() {
    return (window.settings && window.settings.admin_ajax_url) || '/wp-admin/admin-ajax.php';
}

function nonce() {
    return (window.settings && window.settings.cart_nonce) || '';
}

function open(drawer) {
    drawer.classList.add('cart-drawer--active');
    document.body.classList.add('no-scroll');
}

function close(drawer) {
    drawer.classList.remove('cart-drawer--active');
    document.body.classList.remove('no-scroll');
}

function refreshBody(drawer, html) {
    const body = drawer.querySelector('[data-cart-drawer-body]');
    if (body && html) body.innerHTML = html;
}

// Everything else the fc_cart_* actions send back besides the drawer body —
// the header total + item badge ([data-cart-fragment]) — so they follow
// qty/remove/coupon changes and custom adds without a reload. (WooCommerce
// core only applies fragments for its own add-to-cart buttons.)
function applyFragments(fragments) {
    if (!fragments) return;
    Object.keys(fragments).forEach((selector) => {
        if (selector === '.fc-cart-drawer-fragment') return;
        try {
            document.querySelectorAll(selector).forEach((el) => { el.outerHTML = fragments[selector]; });
        } catch (e) {
            // a jQuery-only selector from some plugin — not ours to apply
        }
    });
}

function bindBodyEvents(drawer) {
    const body = drawer.querySelector('[data-cart-drawer-body]');
    if (!body) return;

    body.querySelectorAll('[data-cart-item]').forEach((item) => {
        const key = item.getAttribute('data-cart-item');
        const qtyEl = item.querySelector('.cart-drawer__item-qty span');
        const currentQty = () => parseInt(qtyEl.textContent, 10) || 1;

        const inc = item.querySelector('[data-cart-qty-increase]');
        const dec = item.querySelector('[data-cart-qty-decrease]');
        const remove = item.querySelector('[data-cart-item-remove]');

        if (inc) inc.addEventListener('click', () => updateQty(drawer, key, currentQty() + 1));
        if (dec) dec.addEventListener('click', () => updateQty(drawer, key, currentQty() - 1));
        if (remove) remove.addEventListener('click', () => removeItem(drawer, key));
    });

    const couponForm = body.querySelector('[data-cart-coupon-form]');
    if (couponForm) {
        couponForm.addEventListener('submit', (e) => {
            e.preventDefault();
            const input = couponForm.querySelector('input[name="coupon_code"]');
            if (input && input.value) applyCoupon(drawer, input.value);
        });
    }
}

function postCart(action, data) {
    const body = new URLSearchParams({ action, nonce: nonce(), ...data });
    return fetch(ajaxUrl(), { method: 'POST', body, credentials: 'same-origin' }).then((r) => r.json());
}

function updateQty(drawer, key, qty) {
    postCart('fc_cart_update_qty', { key, qty }).then((res) => {
        if (res && res.success) {
            refreshBody(drawer, res.data.html);
            bindBodyEvents(drawer);
            applyFragments(res.data.fragments);
        }
    });
}

function removeItem(drawer, key) {
    postCart('fc_cart_remove_item', { key }).then((res) => {
        if (res && res.success) {
            refreshBody(drawer, res.data.html);
            bindBodyEvents(drawer);
            applyFragments(res.data.fragments);
        }
    });
}

function applyCoupon(drawer, code) {
    postCart('fc_cart_apply_coupon', { code }).then((res) => {
        if (res && res.success) {
            refreshBody(drawer, res.data.html);
            bindBodyEvents(drawer);
            applyFragments(res.data.fragments);
            const errorEl = drawer.querySelector('[data-cart-coupon-error]');
            if (errorEl) errorEl.textContent = res.data.error || '';
        }
    });
}

// Exported so other AJAX-driven add-to-cart flows (product-page.js's
// bundle-add) can open+refresh the drawer directly instead of going through
// the shared 'added_to_cart' jQuery event below — that event is also
// WooCommerce core's OWN (wc-add-to-cart.js listens on it too), and reusing
// it for a custom button made WC insert its own default "View cart" link
// after that button, which isn't styled (it's not part of this theme's
// markup) and is redundant anyway since the drawer opening already shows
// the cart.
function openWithFragments(fragments) {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    // Key must match includes/woocommerce.php's woocommerce_add_to_cart_fragments
    // filter — see the comment there for why it's deliberately NOT the real
    // '[data-cart-drawer-body]' selector.
    if (fragments && fragments['.fc-cart-drawer-fragment']) {
        refreshBody(drawer, fragments['.fc-cart-drawer-fragment']);
        bindBodyEvents(drawer);
    }
    applyFragments(fragments);
    open(drawer);
}

function init() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    document.querySelectorAll('[data-cart-drawer-toggle]').forEach((toggle) => {
        toggle.addEventListener('click', (e) => {
            e.preventDefault();
            open(drawer);
        });
    });

    drawer.querySelectorAll('[data-cart-drawer-close]').forEach((btn) => {
        btn.addEventListener('click', () => close(drawer));
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close(drawer);
    });

    bindBodyEvents(drawer);

    // WooCommerce's own core add-to-cart script (wc-add-to-cart.js) fires
    // this jQuery event on every successful AJAX add from its OWN buttons
    // (product cards, the real single-product add-to-cart form) — listening
    // for it here is what makes the drawer react to those. See
    // wp-jquery.js for why this needs the real WP-core jQuery instance, not
    // this bundle's own `$`.
    const realJQuery = getRealJQuery();
    if (realJQuery) {
        realJQuery(document.body).on('added_to_cart', (event, fragments) => {
            openWithFragments(fragments);
        });
    }
}

export { openWithFragments };
export default { init };
